'use client';

/**
 * '{카테고리명} 추천 상품' 지면 (몰로코 광고+추천 혼합). MO 전용 (컨트롤러에서 MO 에서만 호출)
 */
import type {
  CategoryMolocoRecommendProduct,
  CategoryMolocoRecommendResult,
} from '@apis/display/categoryMolocoLp';
import useGlobalState from '@hooks/useGlobalState';
import useRenderProducts from '@hooks/useRenderProducts';
import useMolocoLogger from '@hooks/useMolocoLogger';
import { sendMolocoImpTracker } from '@tags/moloco';
import { useCategoryModuleGa } from '@components/pages/category/categoryGa';
import Box from '@wconcept/wconcept-ui-renewal/components/atoms/Box';
import ItemsScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsScrollBar';
import ContTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ContTitle';
import { useCallback, useEffect, useMemo, useRef, type JSX } from 'react';

/** 타이틀 접미사 — 말줄임은 카테고리명에만 */
const TITLE_SUFFIX = '추천 상품';

/** GA affiliation — 몰로코 광고 상품은 moloco, 나머지는 내부 추천 */
const affiliationOf = (product: CategoryMolocoRecommendProduct): string =>
  product.adYn === 'Y' ? 'moloco' : 'recommend';

interface CategoryRecommendProductRowProps {
  readonly result?: CategoryMolocoRecommendResult | null;
}

function CategoryRecommendProductRow({
  result,
}: CategoryRecommendProductRowProps): JSX.Element | null {
  const { appInfo } = useGlobalState();
  // 추천(recommend) + 몰로코 광고(moloco) 혼합 — affiliation 은 상품 adYn 으로
  const { sendSelectItem, getHeartCallbacks } =
    useCategoryModuleGa('recommend');
  const heartCallbacks = useMemo(
    () =>
      getHeartCallbacks(product =>
        affiliationOf(product as CategoryMolocoRecommendProduct)
      ),
    [getHeartCallbacks]
  );
  const { renderProducts } = useRenderProducts(heartCallbacks);
  const { sendMolocoDecisionClickTracker } = useMolocoLogger();

  const products = useMemo(
    () =>
      (result?.productList ?? []).map(product => ({
        ...product,
        productImageUrl:
          product.productImageUrl || product.imageUrlMobile || '',
      })),
    [result?.productList]
  );

  const itemsRef = useRef<HTMLDivElement[]>([]);
  useEffect(() => {
    itemsRef.current.length = products.length;
  }, [products.length]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isTouching = useRef(false);
  const hasScrolled = useRef(false);

  // 보이는 광고 카드만 imp 발화 (스크롤 컨테이너와 1픽셀이라도 겹치는 카드)
  const fireVisibleAdImpressions = useCallback(() => {
    const container = scrollContainerRef.current;
    const targetScrollLeft = container?.scrollLeft || 0;
    const targetScrollRight = targetScrollLeft + (container?.offsetWidth || 0);
    const isVisible = (i: number) => {
      const item = itemsRef.current[i];
      if (!item) {
        return false;
      }
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      return itemRight > targetScrollLeft && itemLeft < targetScrollRight;
    };
    const urls: string[] = [];
    products.forEach((product, i) => {
      if (!isVisible(i)) {
        return;
      }
      const impUrl = product.impTrackerList?.[0];
      if (product.adYn === 'Y' && impUrl) {
        urls.push(impUrl);
      }
    });
    if (urls.length) {
      sendMolocoImpTracker(!!appInfo?.isNativeAppBoth, urls);
    }
  }, [products, appInfo?.isNativeAppBoth]);

  // 섹션이 1픽셀이라도 보이게 될 때마다 (최초 노출, 상하 스크롤 재진입)
  useEffect(() => {
    const callbackFunction = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        fireVisibleAdImpressions();
      }
    };
    const observer = new IntersectionObserver(callbackFunction, {
      threshold: 0,
    });
    let observed: Element | null = null;
    if (containerRef.current) {
      observer.observe(containerRef.current);
      observed = containerRef.current;
    }
    return () => {
      if (observed) {
        observer.unobserve(observed);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fireVisibleAdImpressions]);

  // 가로 스크롤/터치 정지 후 재판정
  const handleTracker = useCallback(() => {
    if (isTouching.current || !hasScrolled.current) {
      return;
    }
    timeoutRef.current = setTimeout(() => {
      fireVisibleAdImpressions();
    }, 200);
  }, [fireVisibleAdImpressions]);

  const onScroll = useCallback(() => {
    hasScrolled.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      handleTracker();
    }, 200);
  }, [handleTracker]);

  const onTouchStart = useCallback(() => {
    isTouching.current = true;
    hasScrolled.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (timeoutRef.current) {
      isTouching.current = false;
      clearTimeout(timeoutRef.current);
      handleTracker();
    }
    hasScrolled.current = false;
  }, [handleTracker]);

  // 상품 클릭 : GA + 광고 클릭 트래커 (이동은 renderProducts 가 처리)
  const onClickProduct = useCallback(
    (product: CategoryMolocoRecommendProduct, index: number) => {
      sendSelectItem(product, index, affiliationOf(product));
      const clickUrl = product.clickTrackerList?.[0];
      if (product.adYn === 'Y' && clickUrl) {
        sendMolocoDecisionClickTracker(clickUrl);
      }
    },
    [sendSelectItem, sendMolocoDecisionClickTracker]
  );

  if (!products.length) {
    return null;
  }

  const productItems = renderProducts({
    products,
    type: 'simple-small',
    isBrandNameKr: true,
    showExclusiveEmblem: true,
    refs: itemsRef,
    isAd: product => (product as CategoryMolocoRecommendProduct).adYn === 'Y',
    onClickProduct: (product, index) =>
      onClickProduct(product as CategoryMolocoRecommendProduct, index),
  });

  const fullTitle = result?.title ?? '';
  const hasSuffix = fullTitle.endsWith(TITLE_SUFFIX);
  const titleName = hasSuffix
    ? fullTitle.slice(0, -TITLE_SUFFIX.length).trimEnd()
    : fullTitle;

  return (
    <Box ref={containerRef} as="section">
      <ContTitle
        title={
          <span className="text-ellipsis-wrapper">
            <span className="text-ellipsis">{titleName}</span>
            {hasSuffix && TITLE_SUFFIX}
          </span>
        }
      />
      <ItemsScrollBar
        ref={scrollContainerRef}
        perView={3.21296}
        rowGap={10}
        colGap={4}
        offsetLR={16}
        onScroll={onScroll}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {productItems}
      </ItemsScrollBar>
    </Box>
  );
}

export default CategoryRecommendProductRow;
